import { MediumSmallTitle, SmallTitle } from '@/components/investors/shared';
import { SeoProps } from '@/components/shared/SEO';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import { Flex, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { Client } from '@lemonade-hq/nuiza';
import { GetStaticProps } from 'next';
import styled from 'styled-components';

const ResultCard = styled(Flex)`
  ${font('main', { fontSize: '1.6rem', lineHeight: '2.4rem' })};
  text-wrap: balance;
  border-radius: 10px;
  background: ${themedColor('cloud')};
  padding: 20px;

  ${MEDIA_QUERIES.desktop`
    width: 100%;
    max-width: 192.5px;
  `}
`;

const CardsWrapper = styled(Flex)`
  flex-direction: column;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const MAX_WIDTH = 800;

const seo = {
  title: 'Lemonade Inc. - Quarterly Results',
  description:
    'View Lemonade’s quarterly earnings, financial statements, and performance highlights to monitor our business progress throughout the year.',
} as SeoProps;

interface QuarterlyResult {
  year: string;
  qs: {
    title: string;
    links: { url: string; label: string }[];
  }[];
}

interface QuarterlyResultsPageProps {
  quarterlyResults: QuarterlyResult[];
}

export default function QuarterlyResultsPage({ quarterlyResults }: QuarterlyResultsPageProps) {
  return (
    <InvestorsPageTemplate seo={seo}>
      <Flex flexDirection="column" width="100%" maxWidth={MAX_WIDTH} gap="30px">
        {quarterlyResults.map(quarterlyResult => (
          <Flex
            flexDirection="column"
            gap="20px"
            pl={[20, 20, 0]}
            pr={[20, 20, 0]}
            width="100%"
            key={quarterlyResult.year}
          >
            <MediumSmallTitle>{quarterlyResult.year}</MediumSmallTitle>

            <CardsWrapper gap="10px" mb={[0, 0, 10]} pb={[10, 10, 0]} flexWrap="no-wrap">
              {quarterlyResult.qs.map(quarter => (
                <ResultCard flexDirection="column" gap="10px" key={quarter.title}>
                  <SmallTitle>{quarter.title}</SmallTitle>

                  <Flex flexDirection="column" gap="2px">
                    {quarter.links.map(link => (
                      <a
                        href={link.url}
                        key={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textTransform: 'capitalize' }}
                      >
                        {link.label}
                      </a>
                    ))}
                  </Flex>
                </ResultCard>
              ))}
            </CardsWrapper>
          </Flex>
        ))}
      </Flex>
    </InvestorsPageTemplate>
  );
}

const getQuarterlyResults = async (): Promise<{ data: QuarterlyResult[] }> => {
  try {
    return await Client.get<{ data: QuarterlyResult[] }>(`${process.env.NEXT_PUBLIC_INVESTORS_URL}/quarterly`);
  } catch (err) {
    console.error('quarterly results error', err);
    return { data: [] };
  }
};

export const getStaticProps: GetStaticProps = async () => {
  const { data: quarterlyResults } = await getQuarterlyResults();
  return {
    props: {
      quarterlyResults,
    },
    revalidate: 10, // 10 seconds, we need this to be wired as much as possible to the data source
  };
};
